import { supabase } from "./supabaseClient";

// ── Mapping label tampilan kategori & brand (sesuai yang sudah ada di
// tabel categories/brands) — dipakai sebagai fallback kalau join gagal. ──
const CATEGORY_LABELS = {
  Display: "Display",
  Conference: "Conference",
  Microphone: "Microphone",
  Speaker: "Speaker",
};

const BRAND_LABELS = {
  samsung: "Samsung",
  aver: "AVer",
  hisense: "Hisense",
  extron: "Extron",
  cisco: "Cisco",
  crestron: "Crestron",
  bose: "Bose",
};

// Mengubah satu row dari tabel `products` (snake_case, sesuai schema
// Supabase) menjadi bentuk objek yang STRUKTURNYA IDENTIK dengan array
// PRODUCTS lama (camelCase) — supaya seluruh kode RoomDesigner.js yang
// sudah ada (filter, render sidebar, geometri 3D via _meta.type3D, dst)
// tetap berjalan tanpa perlu diubah sama sekali.
function mapRowToProduct(row, categoryLabelMap = {}, brandLabelMap = {}) {
  return {
    id: row.id,
    category: categoryLabelMap[row.category_id] || CATEGORY_LABELS[row.category_id] || row.category_id,
    type: row.type,
    brand: brandLabelMap[row.brand_id] || BRAND_LABELS[row.brand_id] || row.brand_id,
    name: row.name,
    width: Number(row.width),
    height: Number(row.height),
    depth: Number(row.depth),
    mountOn: row.mount_on,
    color: row.color,
    accentColor: row.accent_color,
    image: row.image_url || null,
    modelUrl: row.model_url || null,   // dipakai mulai Fase 2 (GLTFLoader)
    modelScale: Number(row.model_scale) || 1,
    modelRotationY: Number(row.model_rotation_y) || 0,
    coverageAngle: Number(row.coverage_angle),
    coverageDistance: Number(row.coverage_distance),
    _meta: row.meta || {},             // PENTING: key tetap "_meta" (bukan "meta")
                                        // karena logic geometri 3D (type3D, cabinetW,
                                        // dll) membaca product._meta, bukan product.meta.
  };
}

/**
 * Ambil daftar kategori dari Supabase (tabel `categories`), terurut
 * sesuai sort_order. Dipakai untuk filter chip "Category" & pengelompokan
 * sidebar di RoomDesigner, supaya begitu admin tambah/ubah/hapus kategori
 * lewat halaman admin, sidebar publik ikut berubah tanpa perlu deploy ulang.
 */
export async function fetchCategories() {
  const { data, error } = await supabase.from("categories").select("*").order("sort_order", { ascending: true });
  if (error) return { categories: [], error };
  return { categories: data || [], error: null };
}

/**
 * Sama seperti fetchCategories(), untuk tabel `brands`.
 */
export async function fetchBrands() {
  const { data, error } = await supabase.from("brands").select("*").order("sort_order", { ascending: true });
  if (error) return { brands: [], error };
  return { brands: data || [], error: null };
}

/**
 * Ambil semua produk AKTIF dari Supabase, sudah dalam bentuk siap pakai
 * untuk RoomDesigner (array objek bergaya PRODUCTS lama).
 * Mengembalikan { products, error }.
 *
 * Label kategori & brand diambil LANGSUNG dari tabel categories/brands
 * (bukan cuma dari CATEGORY_LABELS/BRAND_LABELS di atas) — jadi kalau
 * admin mengganti nama kategori/brand lewat halaman admin, produk yang
 * ditampilkan otomatis ikut memakai nama terbaru. CATEGORY_LABELS/
 * BRAND_LABELS di atas cuma dipakai sebagai fallback terakhir kalau
 * fetch ke tabel referensinya gagal.
 */
export async function fetchProducts() {
  const [{ data: catRows }, { data: brandRows }, { data, error }] = await Promise.all([
    supabase.from("categories").select("id, label"),
    supabase.from("brands").select("id, label"),
    supabase.from("products").select("*").eq("is_active", true).order("name"),
  ]);

  if (error) {
    return { products: [], error };
  }

  const categoryLabelMap = Object.fromEntries((catRows || []).map((c) => [c.id, c.label]));
  const brandLabelMap = Object.fromEntries((brandRows || []).map((b) => [b.id, b.label]));

  return { products: (data || []).map((row) => mapRowToProduct(row, categoryLabelMap, brandLabelMap)), error: null };
}